import { Route, Routes } from 'react-router-dom'
import { observer } from 'mobx-react'
import React, { Suspense, lazy } from 'react'
import { Box, LinearProgress } from '@mui/material'

// Layouts & Guards (kept eager - small, needed immediately for route matching)
import PresenterLayout from '../layout/faculty/PresenterLayout'
import RequireAuditAccess from '../audit/RequireAuditAccess'
import AdminLayout from '../layout/admin/AdminLayout'
import UserLayout from '../layout/student/UserLayout'
import ProtectedFeature from '../../../shared/components/routes/ProtectedFeature'
import RequireAuth from '../../../shared/components/routes/RequireAuth'
import AdmissionExamsRequireAuth from '../admission-exam/layout/AdmissionExamsRequireAuth'
import AdmissionExamLayout from '../admission-exam/layout/AdmissionExamLayout'
import AuditingLayout from '../layout/audit/AuditingLayout'
import ProtectedSubadminComponent from '../../../shared/components/routes/ProtectedSubadminComponent'
import PageNotFound from '../../pages/errors/PageNotFound'
import PlacementTrackerHODandPrincipalAuditLayout from '../../../alive-placements/components/layout/placement-tracker-monitoring/PlacementTrackerHODandPrincipalAuditLayout'

let activeLoads = 0
const listeners = new Set()

const notify = () => listeners.forEach( ( fn ) => fn( activeLoads ) )

const beginLoad = () => {
    activeLoads += 1
    notify()
}

const endLoad = () => {
    activeLoads = Math.max( 0, activeLoads - 1 )
    notify()
}

const progressLazy = ( importer ) =>
    lazy( () => {
        beginLoad()
        return importer().finally( endLoad )
    } )

const TopProgressBar = () => {
    const [width, setWidth] = React.useState( 0 )
    const hideTimeout = React.useRef( null )
    const growTimeout = React.useRef( null )

    React.useEffect( () => {
        const onChange = ( count ) => {

            clearTimeout( hideTimeout.current )
            clearTimeout( growTimeout.current )

            if ( count > 0 ) {
                setWidth( 15 ) // instant jump so it feels responsive
                growTimeout.current = setTimeout( () => setWidth( 75 ), 150 )
            } else {
                setWidth( 100 )
                hideTimeout.current = setTimeout( () => {
                    setWidth( 0 )
                }, 250 )
            }
        }

        listeners.add( onChange )
        return () => {
            listeners.delete( onChange )
            clearTimeout( hideTimeout.current )
            clearTimeout( growTimeout.current )
        }
    }, [] )

    if ( width === 0 ) return null

    return (
        <Box
            component={LinearProgress}
            variant="determinate"
            value={width || 0}
            id="pageload-indicator"
            sx={{
                height: 2,
                position: 'fixed',
                top: 0,
                left: 0,
                width: "100%",
                transition: width === 15 ? 'none' : 'width 300ms ease-out, opacity 200ms ease-out',
                opacity: width === 100 ? 0 : 1,
                zIndex: 9999 * 1000000,
                boxShadow: '0 0 8px rgba(37, 99, 235, 0.6)',
            }}
        />
    )
}

// ---- Lazy-loaded pages ----

// Exams
const StudentParticipationDetails = progressLazy( () => import( '../../pages/exam/student/StudentParticipationDetails' ) )
const AllParticipations = progressLazy( () => import( '../../pages/exam/faculty/AllExamsParticipations' ) )
const ExamParticipation = progressLazy( () => import( '../../pages/exam/faculty/ExamParticipation' ) )
const AddQuestions = progressLazy( () => import( '../../pages/exam/faculty/AddQuestions' ) )
const ExamAttempt = progressLazy( () => import( '../../pages/exam/student/ExamAttempt' ) )
const EditExam = progressLazy( () => import( '../../pages/exam/faculty/EditExam' ) )
const Evaluate = progressLazy( () => import( '../../pages/exam/faculty/Evaluate' ) )
const AddExam = progressLazy( () => import( '../../pages/exam/faculty/AddExam' ) )
const Scores = progressLazy( () => import( '../../pages/exam/student/Scores' ) )
const Exams = progressLazy( () => import( '../../pages/exam/Exams' ) )
const Exam = progressLazy( () => import( '../../pages/exam/Exam' ) )

// Study Material
const AddMaterial = progressLazy( () => import( '../../pages/study-material/AddMaterial' ) )
const FacultyDashboard = progressLazy( () => import( '../../pages/faculty/Dashboard' ) )
const Materials = progressLazy( () => import( '../../pages/study-material/Materials' ) )
const Material = progressLazy( () => import( '../../pages/study-material/Material' ) )
const Notfound = progressLazy( () => import( '../../pages/errors/Notfound' ) )

// Admin
const DepartmentAcademicRuleChecklist = progressLazy( () => import( '../../pages/admin/academic-program/DeparmentAcademicRuleCheklist' ) )
const AcademicProgressAuditors = progressLazy( () => import( '../../pages/admin/academic-program/AcademicProgressAuditors' ) )
const AdminAcademicProgressAudit = progressLazy( () => import( '../../pages/admin/academic-program/AcademicProgressAudit' ) )
const InstituteAcademicRules = progressLazy( () => import( '../../pages/admin/academic-program/InstituteAcademicRules' ) )
const ResumeTemplates = progressLazy( () => import( '../../pages/admin/resume-builder/ResumeTemplates' ) )
const ResumeEntities = progressLazy( () => import( '../../pages/admin/resume-builder/ResumeEntities' ) )
const ResumeTemplate = progressLazy( () => import( '../../pages/admin/resume-builder/ResumeTemplate' ) )
const UserPermissionManager = progressLazy( () => import( '../../pages/admin/UserPermissionManager' ) )
const FeedbackSettings = progressLazy( () => import( '../../pages/admin/feedback/FeedbackSettings' ) )
const Announcements = progressLazy( () => import( '../../pages/admin/announcements/Announcements' ) )
const Announcement = progressLazy( () => import( '../../pages/admin/announcements/Announcement' ) )
const ResumeForms = progressLazy( () => import( '../../pages/admin/resume-builder/ResumeForms' ) )
const ResumeForm = progressLazy( () => import( '../../pages/admin/resume-builder/ResumeForm' ) )
const AttendanceAudit = progressLazy( () => import( '../../pages/admin/AttendanceAudit' ) )
const ManageAccounts = progressLazy( () => import( '../../pages/admin/ManageAccounts' ) )
const ControlPanel = progressLazy( () => import( '../../pages/admin/ControlPanel' ) )
const Analytics = progressLazy( () => import( '../../pages/admin/AdminDashboard' ) )
const EmbeddBbb = progressLazy( () => import( '../../pages/common/EmbeddBbb' ) )
const AdminSignin = progressLazy( () => import( '../../pages/admin/Signin' ) )
const Password = progressLazy( () => import( '../../pages/admin/Password' ) )
const LiveClass = progressLazy( () => import( '../../pages/common/Audit' ) )

// Audit Pages
const AuditStudyMaterialsList = progressLazy( () => import( '../audit/study-materials/AuditStudyMaterialsList' ) )
const AuditQuestionBanksList = progressLazy( () => import( '../audit/questionbanks/AuditQuestionBanksList' ) )
const StudyMaterialsStats = progressLazy( () => import( '../audit/study-materials/StudyMaterialsStats' ) )
const AuditDiscussionsList = progressLazy( () => import( '../audit/discussions/AuditDiscussionsList' ) )
const QuestionBanksStats = progressLazy( () => import( '../audit/questionbanks/QuestionBanksStats' ) )
const AuditStudyMaterials = progressLazy( () => import( '../../pages/audit/AuditStudyMaterials' ) )
const AuditQuestionBanks = progressLazy( () => import( '../../pages/audit/AuditQuestionBanks' ) )
const DiscussionsStats = progressLazy( () => import( '../audit/discussions/DiscussionsStats' ) )
const AssignmentsStats = progressLazy( () => import( '../audit/assignments/AssignmentsStats' ) )
const AuditAssignments = progressLazy( () => import( '../../pages/audit/AuditAssignments' ) )
const AuditPlacementTraining = progressLazy( () => import( '../../pages/audit/AuditPlacementTraining' ) )
const AuditDiscussions = progressLazy( () => import( '../../pages/audit/AuditDiscussions' ) )
const AuditQuizzesList = progressLazy( () => import( '../audit/quizzes/AuditQuizzesList' ) )
const AssignmentList = progressLazy( () => import( '../audit/assignments/AssignmentList' ) )
const AuditDashboard = progressLazy( () => import( '../../pages/audit/AuditDashboard' ) )
const AuditExamsList = progressLazy( () => import( '../audit/exams/AuditExamsList' ) )
const AuditQuizzes = progressLazy( () => import( '../../pages/audit/AuditQuizzes' ) )
const FacultyAudit = progressLazy( () => import( '../../pages/audit/FacultyAudit' ) )
const QuizzesStats = progressLazy( () => import( '../audit/quizzes/QuizzesStats' ) )
const AuditExams = progressLazy( () => import( '../../pages/audit/AuditExams' ) )
const ExamsStats = progressLazy( () => import( '../audit/exams/ExamsStats' ) )

// Placement Tracker Pages
const PlacementMonitoringStudents = progressLazy( () => import( '../../../alive-placements/pages/placement-tracker/monitoring/StudentsMonitoring' ) )
const PlacementMonitoringJobs = progressLazy( () => import( '../../../alive-placements/pages/placement-tracker/monitoring/JobsMonitoring' ) )
const PlacementMonitoringJobDetail = progressLazy( () => import( '../../../alive-placements/pages/placement-tracker/monitoring/JobDetailMonitoring' ) )

// Discussions
const MyDiscussions = progressLazy( () => import( '../../pages/forum/MyDiscussions' ) )
const Discussions = progressLazy( () => import( '../../pages/forum/Discussions' ) )
const Discussion = progressLazy( () => import( '../../pages/forum/Discussion' ) )

// Students Resume Builder
const ResumeBuilderForm = progressLazy( () => import( '../../pages/student/resume-builder/ResumeBuilderForm' ) )
const ResumePreview = progressLazy( () => import( '../../pages/student/resume-builder/ResumePreview' ) )
const SubjectClassSessions = progressLazy( () => import( '../../pages/subject/SubjectClassSessions' ) )
const SubjectMaterials = progressLazy( () => import( '../../pages/subject/SubjectStudyMaterials' ) )
const SubjectDiscussions = progressLazy( () => import( '../../pages/subject/SubjectDiscussions' ) )
const SubjectAssignments = progressLazy( () => import( '../../pages/subject/SubjectAssignments' ) )
// eslint-disable-next-line no-unused-vars
const AiMentorFaculty = progressLazy( () => import( '../../pages/subject/AiMentorFaculty' ) )
const SubjectDashboard = progressLazy( () => import( '../../pages/subject/SubjectFeeds' ) )
const SubjectClasses = progressLazy( () => import( '../../pages/subject/SubjectClasses' ) )
const SubjectExams = progressLazy( () => import( '../../pages/subject/SubjectExams' ) )
const ClassSession = progressLazy( () => import( '../../pages/subject/ClassSession' ) )
const Subjects = progressLazy( () => import( '../../pages/common/Subjects' ) )
const Subject = progressLazy( () => import( '../../pages/common/Subject' ) )

// Quiz and questionbanks
const QuestionBanks = progressLazy( () => import( '../../pages/faculty/questionbank/QuestionBanks' ) )
const QuestionBank = progressLazy( () => import( '../../pages/faculty/questionbank/QuestionBank' ) )
const StudentQuiz = progressLazy( () => import( '../../pages/student/quiz/StudentQuiz' ) )
const QuizAttempt = progressLazy( () => import( '../../pages/student/quiz/QuizAttempt' ) )
const Quizzes = progressLazy( () => import( '../../pages/faculty/quiz/Quizzes' ) )
const Quiz = progressLazy( () => import( '../../pages/faculty/quiz/Quiz' ) )

// Faculty Pages
const EvaluateAssignment = progressLazy( () => import( '../../pages/faculty/EvaluateAssignment' ) )
const PresenterRecording = progressLazy( () => import( '../../pages/faculty/Recording' ) )
const Assignment = progressLazy( () => import( '../../pages/faculty/Assignment' ) )

// Students
const SessionFeedback = progressLazy( () => import( '../../pages/student/SessionFeedback' ) )
const StudentDashboard = progressLazy( () => import( '../../pages/student/Dashboard' ) )
const UserAssignment = progressLazy( () => import( '../../pages/student/Assignment' ) )
const Leaderboard = progressLazy( () => import( '../../pages/student/Leaderboard' ) )

// General Pages
const StreamSessionEnded = progressLazy( () => import( '../../pages/common/StreamSessionEnded' ) )
const NetworkError = progressLazy( () => import( '../../pages/errors/NetworkError' ) )
const ExamNotfound = progressLazy( () => import( '../../pages/errors/ExamNotFound' ) )
const FAQs = progressLazy( () => import( '../../pages/common/FAQs' ) )

// Admission exams
const AdmissionExamWorksheetUpload = progressLazy( () => import( '../../pages/admission-exam/AdmissionExamWorksheetUpload' ) )
const CandidateParticipationResult = progressLazy( () => import( '../../pages/admission-exam/CandidateParticipationResult' ) )
const AdmissionExamParticipation = progressLazy( () => import( '../../pages/admission-exam/ExamParticipation' ) )
const AdmissionExamEvaluate = progressLazy( () => import( '../../pages/admission-exam/AdmissionExamEvaluate' ) )
const AdmissionExamAttempt = progressLazy( () => import( '../../pages/admission-exam/AdmissionExamAttempt' ) )
const CreateAdmissionExam = progressLazy( () => import( '../../pages/admission-exam/CreateAdmissionExam' ) )
const AdmissionExams = progressLazy( () => import( '../../pages/admission-exam/AdmissionExams' ) )
const CandidateLogin = progressLazy( () => import( '../../pages/admission-exam/CandidateLogin' ) )
const CandidateExams = progressLazy( () => import( '../../pages/admission-exam/CandidateExams' ) )
const AdmissionExam = progressLazy( () => import( '../../pages/admission-exam/AdmissionExam' ) )
const ThankYouPage = progressLazy( () => import( '../../pages/admission-exam/ThankYouPage' ) )

// Signin / Meetings / Misc
const ERPSessionSingin = progressLazy( () => import( '../../pages/common/ERPSessionSignin' ) )
const DebugLogs = progressLazy( () => import( '../../pages/admin/logging/DebugLogs' ) )
const AuditClassSessions = progressLazy( () => import( '../../pages/audit/AuditClassSessions' ) )
const ClassSessionStats = progressLazy( () => import( '../audit/class-sessions/ClassSessionsStats' ) )
const AuditClassSessionsList = progressLazy( () => import( '../audit/class-sessions/ClassSessionsList' ) )
const WebinarRoom = progressLazy( () => import( '../../pages/meetings/WebinarRoom' ) )
const MeetingRecorder = progressLazy( () => import( '../../pages/meetings/MeetingRecorder' ) )
const OnlineMeetings = progressLazy( () => import( '../../pages/meetings/OnlineMeetings' ) )
const OnlineMeetingDetails = progressLazy( () => import( '../../pages/meetings/OnlineMeetingDetails' ) )
const AliveLogin = progressLazy( () => import( '../../pages/common/AliveLogin' ) )
const OnlineMeetingsHome = progressLazy( () => import( '../../pages/meetings/OnlineMeetingsHome' ) )
const AuditMeetings = progressLazy( () => import( '../../pages/audit/AuditMeetings' ) )
const MeetingsStats = progressLazy( () => import( '../audit/meetings/MeetingsStats' ) )
const MeetingFeedback = progressLazy( () => import( '../../pages/meetings/MeetingFeedback' ) )
const MeetingsList = progressLazy( () => import( '../audit/meetings/MeetingList' ) )
const FacultyMaterials = progressLazy( () => import( '../../pages/study-material/FacultyMaterials' ) )
const PracticeWithSimba = progressLazy( () => import( '../../pages/faculty/practice-with-simba/PracticeWithSimba' ) )
const PracticeSession = progressLazy( () => import( '../../pages/faculty/practice-with-simba/PracticeSession' ) )
const AuditSimbaAi = progressLazy( () => import( '../../pages/audit/AuditSimbaAi' ) )
const PracticeSessionsStats = progressLazy( () => import( '../audit/practice-with-simba/PracticeSessionsStats' ) )
const AuditPracticeSessionsList = progressLazy( () => import( '../audit/practice-with-simba/AuditPracticeSessionsList' ) )
const FeaturePermissions = progressLazy( () => import( '../../pages/admin/FeaturePermissions' ) )
const Unauthorized = progressLazy( () => import( '../../../shared/pages/Unauthorized' ) )
const Impersonation = progressLazy( () => import( '../../pages/admin/Impersonation' ) )
const ImpersonationSingin = progressLazy( () => import( '../../pages/common/ImpersonationSignin' ) )
const AdminActivityLogs = progressLazy( () => import( '../../pages/admin/logging/AdminActivityLogs' ) )
const AuditPracticeSessionsIssues = progressLazy( () => import( '../audit/practice-with-simba/AuditPracticeSessionsIssues' ) )
const HybridClassDetailsForStudent = progressLazy( () => import( '../../pages/meetings/HybridClassDetailsForStudent' ) )
const ScheduledWorkers = progressLazy( () => import( '../../pages/admin/ScheduledWorkers' ) )

const AppRoutes = observer( () => {

    return (
        <>
            <TopProgressBar />
            <Suspense fallback={null}>
                <Routes>
                    <Route exact path='/stream-ended' element={<StreamSessionEnded />} />
                    <Route path='/assessment/login' element={<CandidateLogin />} />
                    <Route exact path='/session/:access_token' element={<ERPSessionSingin />} />
                    <Route exact path='/impersonation/:impersonation_type/:access_token' element={<ImpersonationSingin />} />
                    <Route path='/admin/login' element={<AdminSignin />} />
                    <Route path='/admin' element={<AdminSignin />} />
                    <Route exact path='/' element={<AliveLogin />} />


                    <Route path="/meetings" element={<OnlineMeetingsHome />} />
                    <Route path="/webinar/:meeting_id" element={<WebinarRoom />} />
                    <Route path="/meeting/:meeting_id" element={<WebinarRoom />} />
                    <Route path="/class/:meeting_id" element={<WebinarRoom />} />
                    <Route path="/hybrid-class/:meeting_id" element={<WebinarRoom />} />
                    <Route path="/webinar/:meeting_id/record" element={<MeetingRecorder />} />
                    <Route path="/meeting/:meeting_id/record" element={<MeetingRecorder />} />
                    <Route path="/class/:meeting_id/record" element={<MeetingRecorder />} />
                    <Route path="/hybrid-class/:meeting_id/record" element={<MeetingRecorder />} />
                    <Route path="/webinar/:meeting_id/feedback" element={<MeetingFeedback />} />
                    <Route path="/meeting/:meeting_id/feedback" element={<MeetingFeedback />} />
                    <Route path="/class/:meeting_id/feedback" element={<MeetingFeedback />} />

                    <Route element={<AdmissionExamsRequireAuth allowedRoles={["STUDENT"]} />}>
                        <Route element={<AdmissionExamLayout />}>
                            <Route path="/thankyou" exact element={<ThankYouPage />} />
                            <Route path="/assessment/:id/:pid/attempt/:attempt" exact element={<AdmissionExamAttempt />} />
                            <Route path="/assessment/:id/:pid/worksheet-upload" exact element={<AdmissionExamWorksheetUpload />} />
                            <Route path="/assessment/:id/:pid/score" exact element={<CandidateParticipationResult />} />
                            <Route path="/assessment/" exact element={<CandidateExams />} />
                        </Route>
                    </Route>

                    <Route element={<RequireAuth allowedRoles={["ADMIN", "SUBADMIN"]} />}>
                        <Route element={<AdminLayout />} >

                            <Route path="/admin/assessment/" exact element={<AdmissionExams />} />
                            <Route path="/admin/assessment/new" exact element={<CreateAdmissionExam />} />
                            <Route path="/admin/assessment/:id" exact element={<AdmissionExam />} />
                            <Route path="/admin/assessment/:id/participation" exact element={<AdmissionExamParticipation />} />
                            <Route path="/admin/assessment/:id/evaluate/:participation_id" exact element={<AdmissionExamEvaluate />} />

                            <Route path='/admin/debug-logs' exact element={<DebugLogs />} />
                            <Route path='/admin/activity-logs' exact element={<AdminActivityLogs />} />

                            <Route path='/admin/resume-forms' element={<ResumeForms />} />
                            <Route path='/admin/resume-forms/:form_id' element={<ResumeForm />} />
                            <Route path='/admin/resume-entities' element={<ResumeEntities />} />
                            <Route path='/admin/resume-templates' element={<ResumeTemplates />} />
                            <Route path='/admin/resume-templates/:template_id' element={<ResumeTemplate />} />

                            <Route path='/admin/academic-rules' element={<InstituteAcademicRules />} />
                            <Route path='/admin/academics/audit' element={<AdminAcademicProgressAudit />} />
                            <Route path='/admin/academics/auditors' element={<AcademicProgressAuditors />} />
                            <Route path='/admin/academic-rule/:id/checklist' element={<DepartmentAcademicRuleChecklist />} />

                            <Route path='/admin/announcement' element={<Announcements />} />
                            <Route path='/admin/announcement/:announcement_id' element={<Announcement />} />

                            <Route path='/admin/home' element={<Analytics />} />
                            <Route path='/admin/class-audit' element={<LiveClass />} />
                            <Route path='/admin/password' element={<Password />} />
                            <Route path='/admin/accounts' element={<ManageAccounts />} />
                            <Route path='/admin/attendance' element={<AttendanceAudit />} />
                            <Route path='/admin/control-panel' element={<ControlPanel />} />

                            <Route path='/admin/module-permissions' element={<FeaturePermissions />} />
                            <Route path='/admin/user-permissions' element={<UserPermissionManager />} />

                            <Route path='/admin/impersonation' element={
                                <ProtectedSubadminComponent permissionKey="impersonation">
                                    <Impersonation />
                                </ProtectedSubadminComponent>
                            } />
                            <Route path='/admin/job-workers' element={
                                <ProtectedSubadminComponent permissionKey="workers">
                                    <ScheduledWorkers />
                                </ProtectedSubadminComponent>
                            } />

                            {/* AUDITING */}
                            <Route path='/admin/audit' element={<AuditDashboard />} />
                            <Route path='/admin/assignments' element={<AuditAssignments />} >
                                <Route index element={<AssignmentsStats />} />
                                <Route path='list' element={<AssignmentList />} />
                            </Route>
                            <Route path='/admin/meetings' element={<AuditMeetings key="meetings" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                                <Route path=':meeting_id' element={<OnlineMeetingDetails />} />
                            </Route>
                            <Route path='/admin/online-classes' element={<AuditMeetings key="classes" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                            </Route>
                            <Route path='/admin/hybrid-classes' element={<AuditMeetings key="hybrid-classes" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                            </Route>

                            <Route path='/admin/meetings/:meeting_id' element={<OnlineMeetingDetails />} />
                            <Route path='/admin/online-classes/:meeting_id' element={<OnlineMeetingDetails />} />
                            <Route path='/admin/hybrid-classes/:meeting_id' element={<OnlineMeetingDetails />} />

                            <Route path='/admin/exams' element={<AuditExams />} >
                                <Route index element={<ExamsStats />} />
                                <Route path='list' element={<AuditExamsList />} />
                            </Route>
                            <Route path='/admin/study-materials' element={<AuditStudyMaterials />} >
                                <Route index element={<StudyMaterialsStats />} />
                                <Route path='list' element={<AuditStudyMaterialsList />} />
                            </Route>
                            <Route path='/admin/quizzes' element={<AuditQuizzes />} >
                                <Route index element={<QuizzesStats />} />
                                <Route path='list' element={<AuditQuizzesList />} />
                            </Route>
                            <Route path='/admin/questionbanks' element={<AuditQuestionBanks />} >
                                <Route index element={<QuestionBanksStats />} />
                                <Route path='list' element={<AuditQuestionBanksList />} />
                            </Route>
                            <Route path='/admin/class-sessions' element={<AuditClassSessions />} >
                                <Route index element={<ClassSessionStats />} />
                                <Route path='list' element={<AuditClassSessionsList />} />
                            </Route>
                            <Route path='/admin/discussions' element={<AuditDiscussions />} >
                                <Route index element={<DiscussionsStats />} />
                                <Route path='list' element={<AuditDiscussionsList />} />
                            </Route>
                            <Route path='/admin/practice-with-simba' element={<AuditSimbaAi />} >
                                <Route index element={<PracticeSessionsStats />} />
                                <Route path='list' element={<AuditPracticeSessionsList />} />
                            </Route>
                            <Route path='/admin/practice-with-simba/issues' element={<ProtectedSubadminComponent permissionKey="auditing">
                                <AuditPracticeSessionsIssues />
                            </ProtectedSubadminComponent>} />

                            <Route path='/admin/discussions' element={<AuditDiscussions />} >
                                <Route index element={<DiscussionsStats />} />
                                <Route path='list' element={<AuditDiscussionsList />} />
                            </Route>
                            <Route path='/admin/faculty/:empcode' element={<FacultyAudit />} />

                            <Route path='/admin/feedback-setting' element={<FeedbackSettings />} />
                            <Route path='/admin/networkerror' element={<NetworkError />} />
                        </Route>
                    </Route>



                    <Route element={<RequireAuditAccess allowedRoles={['HOD', 'PRINCIPAL', 'ADMIN', 'SUBADMIN', 'is_auditor']} />}>
                        <Route element={<AuditingLayout />}>
                            <Route path='/audit' element={<Analytics />} />
                            <Route path='/audit/assignments' element={<AuditAssignments />} >
                                <Route index element={<AssignmentsStats />} />
                                <Route path='list' element={<AssignmentList />} />
                            </Route>
                            <Route path='/audit/placement-training' element={<AuditPlacementTraining />} />
                            <Route path='/audit/meetings' element={<AuditMeetings key="class" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                                <Route path=':meeting_id' element={<OnlineMeetingDetails />} />
                            </Route>
                            <Route path='/audit/online-classes' element={<AuditMeetings key="meetings" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                                <Route path=':meeting_id' element={<OnlineMeetingDetails />} />
                            </Route>
                            <Route path='/audit/hybrid-classes' element={<AuditMeetings key="hybrid-classes" />} >
                                <Route index element={<MeetingsStats />} />
                                <Route path='list' element={<MeetingsList />} />
                                <Route path=':meeting_id' element={<OnlineMeetingDetails />} />
                            </Route>
                            <Route path='/audit/exams' element={<AuditExams />} >
                                <Route index element={<ExamsStats />} />
                                <Route path='list' element={<AuditExamsList />} />
                            </Route>
                            <Route path='/audit/study-materials' element={<AuditStudyMaterials />} >
                                <Route index element={<StudyMaterialsStats />} />
                                <Route path='list' element={<AuditStudyMaterialsList />} />
                            </Route>
                            <Route path='/audit/quizzes' element={<AuditQuizzes />} >
                                <Route index element={<QuizzesStats />} />
                                <Route path='list' element={<AuditQuizzesList />} />
                            </Route>
                            <Route path='/audit/questionbanks' element={<AuditQuestionBanks />} >
                                <Route index element={<QuestionBanksStats />} />
                                <Route path='list' element={<AuditQuestionBanksList />} />
                            </Route>
                            <Route path='/audit/class-sessions' element={<AuditClassSessions />} >
                                <Route index element={<ClassSessionStats />} />
                                <Route path='list' element={<AuditClassSessionsList />} />
                            </Route>
                            <Route path='/audit/discussions' element={<AuditDiscussions />} >
                                <Route index element={<DiscussionsStats />} />
                                <Route path='list' element={<AuditDiscussionsList />} />
                            </Route>
                            <Route path='/audit/practice-with-simba' element={<AuditSimbaAi />} >
                                <Route index element={<PracticeSessionsStats />} />
                                <Route path='list' element={<AuditPracticeSessionsList />} />
                            </Route>


                            <Route path='/audit/faculty/:empcode' element={<FacultyAudit />} />
                        </Route>
                        <Route exact path='/audit/unauthorized' element={<Unauthorized />} />
                    </Route>

                    <Route element={<RequireAuth allowedRoles={["HOD", "PRESENTER", "FACULTY", "PRINCIPAL"]} />}>
                        <Route element={<PresenterLayout />}>

                            <Route path='/faculty/audit' element={<LiveClass />} />
                            <Route path='/faculty/stream' element={<EmbeddBbb />} />
                            <Route path='/faculty/home' element={<FacultyDashboard />} />
                            <Route path='/faculty/assignment' element={<Assignment />} />
                            <Route path='/faculty/recordings' element={<PresenterRecording />} />
                            <Route path='/faculty/assignment/:assignment_id' element={<EvaluateAssignment />} />

                            <Route path="/faculty/online-classes" element={<OnlineMeetings key="class" meetingType='class' />} />
                            <Route path="/faculty/hybrid-classes" element={<OnlineMeetings key="hybrid-class" meetingType='hybrid-class' />} />
                            <Route path="/faculty/meetings" element={<OnlineMeetings key="meetings" />} />
                            <Route path="/faculty/meeting/:meeting_id" element={<OnlineMeetingDetails />} />
                            <Route path="/faculty/class/:meeting_id" element={<OnlineMeetingDetails />} />
                            <Route path="/faculty/webinar/:meeting_id" element={<OnlineMeetingDetails />} />
                            <Route path="/faculty/hybrid-class/:meeting_id" element={<OnlineMeetingDetails />} />

                            <Route path='/faculty/class-sessions' element={<SubjectClassSessions />} />

                            <Route path='/faculty/material' element={<FacultyMaterials />} />
                            <Route path='/faculty/material/add' element={<AddMaterial />} />
                            <Route path='/faculty/material/:material_id' element={<Material />} />

                            <Route path='/faculty/quizzes' element={<Quizzes fromComponent="page" />} />
                            <Route path='/faculty/quizzes/:id' element={<Quiz fromComponent="page" />} />
                            <Route path='/faculty/questionbank' element={<QuestionBanks fromMainPage />} />
                            <Route path='/faculty/questionbank/:bank_id' element={<QuestionBank fromMainPage />} />

                            <Route path='/faculty/exams' element={<Exams />} />
                            <Route path='/faculty/exams/:id' element={<Exam />} />
                            <Route path='/faculty/exams/:id/edit' element={<EditExam />} />
                            <Route path='/faculty/participations' element={<AllParticipations />} />
                            <Route path='/faculty/exams/:id/addquestions' element={<AddQuestions />} />
                            <Route path='/faculty/exams/:id/participation' element={<ExamParticipation />} />
                            <Route path='/faculty/exams/:id/evaluate/:participation_id' element={<Evaluate />} />

                            <Route path='/faculty/discussions' element={<Discussions />} >
                                <Route path=':id' element={<Discussion fromComponent />} />
                            </Route>
                            <Route path='/faculty/my-discussions' element={<MyDiscussions />} >
                                <Route path=':id' element={<Discussion fromComponent />} />
                            </Route>
                            <Route path='/faculty/discussion/:id' element={<Discussion />} />

                            <Route path='/faculty/practice-with-simba' element={
                                <ProtectedFeature featureKey="simba_ai" ><PracticeWithSimba /></ProtectedFeature>
                            } />
                            <Route path='/faculty/practice-with-simba/:id' element={
                                <ProtectedFeature featureKey="simba_ai" ><PracticeSession /></ProtectedFeature>
                            } />

                            <Route path='/faculty/notfound' element={<Notfound />} />
                            <Route path='/faculty/examnotfound' element={<ExamNotfound />} />
                            <Route path='/faculty/networkerror' element={<NetworkError />} />
                            <Route exact path='/faculty/faq' element={<FAQs auth="faculty" />} />

                            <Route exact path='/faculty/unauthorized' element={<Unauthorized />} />

                        </Route>
                    </Route>

                    <Route element={<RequireAuth allowedRoles={["STUDENT"]} />}>
                        <Route element={<UserLayout />}>

                            <Route path='/student/home' element={<StudentDashboard />} />
                            <Route path='/student/stream' element={<EmbeddBbb />} />
                            <Route path='/student/dashboard' element={<StudentDashboard />} />
                            <Route path='/student/feedback' element={<SessionFeedback />} />
                            <Route path='/student/assignment' element={<UserAssignment />} />

                            <Route path='/student/subjects' element={<Subjects />} />
                            <Route path='/student/subject/:subject_code' element={<Subject />} >
                                <Route path='home' element={<SubjectDashboard />} />
                                <Route path='class-sessions' element={<SubjectClassSessions />} />
                                <Route path='class-sessions/:session_id' element={<ClassSession />} />

                                <Route path='classes' element={<SubjectClasses />} />
                                <Route path='assignments' element={<SubjectAssignments />} />
                                <Route path='exams' element={<SubjectExams />} />
                                <Route path='exams/:id' element={<Exam fromSubject />} />
                                <Route path='discussions' element={<SubjectDiscussions />} >
                                    <Route path=':id' element={<Discussion fromComponent />} />
                                </Route>
                                <Route path='materials' element={<SubjectMaterials />} />
                                <Route path='materials/:material_id' element={<Material fromSubject />} />
                            </Route>

                            <Route path='/student/class-sessions' element={<SubjectClassSessions />} />

                            <Route path='/student/leaderboard' element={<Leaderboard />} />

                            <Route path='/student/material' element={<Materials />} />
                            <Route path='/student/material/:material_id' element={<Material />} />

                            <Route path="/student/hybrid-classes" element={<OnlineMeetings key="hybrid-class" meetingType='hybrid-class' />} />
                            <Route path="/student/hybrid-class/:meeting_id" element={<HybridClassDetailsForStudent />} />

                            <Route path='/student/exams' element={<Exams />} />
                            <Route path='/student/scores/:exam_id' element={<Scores />} />
                            <Route path='/student/exams/:id' element={<Exam />} />
                            <Route path='/student/notfound' element={<Notfound />} />
                            <Route path='/student/examnotfound' element={<ExamNotfound />} />
                            <Route path='/student/exams/:id/attempt/:attempt/:pid' element={<ExamAttempt />} />
                            <Route path='/student/scores/:exam_id/:participation_id' element={<StudentParticipationDetails />} />

                            <Route path='/student/quiz' element={<StudentQuiz />} />
                            <Route path='/student/quiz/:quiz_id/:instance_id/attempt/:attempt_id' element={<QuizAttempt />} />
                            <Route path='/student/hybrid-class/:hybrid_class_id/quiz/:quiz_id/:instance_id/attempt/:attempt_id' element={<QuizAttempt />} />

                            <Route path='/student/resume-builder' element={<ResumeBuilderForm />} />
                            <Route path='/student/resume-builder/preview' element={<ResumePreview />} />

                            <Route path='/student/discussions' element={<Discussions />} >
                                <Route path=':id' element={<Discussion fromComponent />} />
                            </Route>
                            <Route path='/student/discussion/:id' element={<Discussion />} />
                            <Route path='/student/my-discussions' element={<MyDiscussions />} >
                                <Route path=':id' element={<Discussion fromComponent />} />
                            </Route>

                            <Route path='/student/networkerror' element={<NetworkError />} />
                            <Route exact path='/student/faq' element={<FAQs auth="student" />} />

                            <Route exact path='/student/unauthorized' element={<Unauthorized />} />

                        </Route>
                    </Route>

                    <Route path="*" element={<PageNotFound />} />
                </Routes>
            </Suspense></>
    )
} )

export default AppRoutes